import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { createUserWithProfile } from '@/lib/admin-functions'
import { generatePassword } from '@/lib/utils'
import type { Business } from '@/types'

const MIN_PASSWORD_LENGTH = 8

interface FormData {
  firstName: string
  lastName: string
  email: string
  password: string
  role: string
  businessId: string
  /** Must be ticked to create another administrator. */
  confirmAdmin: boolean
}

interface CreateUserFormProps {
  businesses: Array<Pick<Business, 'id' | 'name'>>
  onSuccess: () => void
  onCancel: () => void
}

/** Map an adminCreateUser failure to a catalog key; the function's own text is English-only. */
function createErrorKey(err: unknown): string {
  const code = (err as { code?: string })?.code ?? ''
  const message = (err as { message?: string })?.message ?? ''
  if (code === 'functions/permission-denied') return 'users.form.errors.notAdmin'
  if (/already (in use|exists)/i.test(message)) return 'users.form.errors.emailInUse'
  if (/invalid email|email address is improperly/i.test(message)) return 'users.form.errors.invalidEmail'
  if (/password/i.test(message)) return 'users.form.errors.weakPassword'
  return 'users.form.createFailed'
}

export function CreateUserForm({ businesses, onSuccess, onCancel }: CreateUserFormProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { register, handleSubmit, setValue, control, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      role: 'care_recipient',
      password: '',
      confirmAdmin: false,
    },
  })
  const creatingAdmin = useWatch({ control, name: 'role' }) === 'admin'

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    setError('')
    try {
      await createUserWithProfile({
        email: data.email.trim(),
        password: data.password,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        role: data.role,
        businessId: data.businessId,
      })
      onSuccess()
    } catch (err) {
      setError(t(createErrorKey(err)))
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          id="firstName"
          label={t('users.form.firstName')}
          {...register('firstName', { required: t('common.required') })}
          error={errors.firstName?.message}
        />
        <Input
          id="lastName"
          label={t('users.form.lastName')}
          {...register('lastName', { required: t('common.required') })}
          error={errors.lastName?.message}
        />
      </div>

      <Input
        id="email"
        label={t('users.form.email')}
        type="email"
        {...register('email', { required: t('common.required') })}
        error={errors.email?.message}
      />

      <div className="space-y-1">
        <label htmlFor="role" className="block text-sm font-medium text-gray-700">{t('users.form.role')}</label>
        <select
          id="role"
          {...register('role', { required: t('common.required') })}
          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="care_recipient">{t('roles.care_recipient')}</option>
          <option value="caregiver">{t('roles.caregiver')}</option>
          <option value="admin">{t('roles.admin')}</option>
        </select>
      </div>

      {creatingAdmin && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <p>{t('users.form.adminWarning')}</p>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              {...register('confirmAdmin', {
                validate: (v, values) => values.role !== 'admin' || v || t('users.form.adminConfirmRequired'),
              })}
            />
            <span>{t('users.form.adminConfirm')}</span>
          </label>
          {errors.confirmAdmin && <p className="text-xs text-red-600">{errors.confirmAdmin.message}</p>}
        </div>
      )}

      <div className="space-y-1">
        <label htmlFor="businessId" className="block text-sm font-medium text-gray-700">{t('users.form.business')}</label>
        <select
          id="businessId"
          {...register('businessId', { required: t('common.required') })}
          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">{t('users.form.selectBusiness')}</option>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        {errors.businessId && (
          <p className="text-xs text-red-600">{errors.businessId.message}</p>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">{t('users.form.tempPassword')}</label>
          <button
            type="button"
            onClick={() => setValue('password', generatePassword())}
            className="text-xs text-primary-600 hover:text-primary-700"
          >
            {t('users.form.generate')}
          </button>
        </div>
        <Input
          id="password"
          {...register('password', {
            required: t('common.required'),
            minLength: {
              value: MIN_PASSWORD_LENGTH,
              message: t('users.form.minLength', { count: MIN_PASSWORD_LENGTH }),
            },
          })}
          error={errors.password?.message}
        />
      </div>

      <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? t('users.form.creating') : t('users.form.create')}
        </Button>
      </div>
    </form>
  )
}
